import React, { useEffect, useState } from 'react';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

const split = (s) => s.split(/[,;\s]+/).map(x => x.trim()).filter(Boolean);

// Email the (already saved) commercial invoice PDF to customer / broker.
export const CiEmailDialog = ({ api, base, open, onOpenChange, invoiceNumber }) => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) { setF(null); return; }
    api().get(`${base}/email-defaults`).then(r => setF({
      to: r.data.to.join(', '), cc: r.data.cc.join(', '), subject: r.data.subject,
      message: `Hello,\n\nPlease find attached Commercial Invoice ${invoiceNumber} for customs clearance.\n\nThank you`,
    })).catch(() => toast.error('Could not load recipients'));
  }, [open, base, invoiceNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    const to = split(f.to);
    if (!to.length) { toast.error('Add at least one recipient'); return; }
    setBusy(true);
    try {
      await api().post(`${base}/email`, { to, cc: split(f.cc), subject: f.subject, message: f.message });
      toast.success(`Commercial invoice sent to ${to.join(', ')}`);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Email failed'); }
    finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ci-email-dialog">
        <DialogHeader><DialogTitle>Email {invoiceNumber}</DialogTitle></DialogHeader>
        {!f ? <div className="py-8 text-center text-sm text-slate-500">Loading…</div> : (
          <div className="space-y-3">
            <div><Label>To *</Label><Input value={f.to} onChange={set('to')} placeholder="customer@example.com" className="rounded-sm" data-testid="ci-email-to" /></div>
            <div><Label>CC (customs broker)</Label><Input value={f.cc} onChange={set('cc')} placeholder="broker@example.com" className="rounded-sm" data-testid="ci-email-cc" /></div>
            <div><Label>Subject</Label><Input value={f.subject} onChange={set('subject')} className="rounded-sm" data-testid="ci-email-subject" /></div>
            <div><Label>Message</Label><Textarea rows={5} value={f.message} onChange={set('message')} className="rounded-sm text-sm" data-testid="ci-email-message" /></div>
            <p className="text-[11px] text-slate-500">The commercial invoice is attached as <span className="mono">{invoiceNumber}.pdf</span>.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="ci-email-cancel">Cancel</Button>
          <Button onClick={send} disabled={busy || !f} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="ci-email-send"><Send className="w-3.5 h-3.5 mr-1" />{busy ? 'Sending…' : 'Send'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CiEmailDialog;
